/* SEO Article Registry — also doubles as the knowledge-graph backbone.
 *
 * Each article carries `tags` so we can auto-compute related content across
 * groups (a Behavior page can auto-surface related Medications, Benefits,
 * Hard Conversations, etc. without anyone manually maintaining the list).
 * Tags are free-form lowercase keywords drawn from the dementia-care domain.
 *
 * New articles can be added inline below OR appended via /lib/seoArticles*.js
 * batch files — the export merges them all so a 25-article batch doesn't
 * require touching this file's edits.
 */
import { ARTICLES_BATCH_2 } from "@/lib/seoArticlesBatch2";
import { ARTICLES_BATCH_3 } from "@/lib/seoArticlesBatch3";

export const ARTICLE_GROUPS = {
    learn: { label: "Learning", href: "/education" },
    behavior: { label: "Behavior Guidance", href: "/behavior" },
    guide: { label: "GUIDE", href: "/guide" },
    "paying-for-care": { label: "Paying for Care", href: "/who-pays" },
    resources: { label: "Resources", href: "/help-finder" },
    treatments: { label: "Treatments", href: "/treatments" },
    "hard-conversations": { label: "Hard Conversations", href: "/conversations" },
};

const ARTICLES_INLINE = [
    // ===== LEARN =====
    {
        slug: "what-is-dementia",
        group: "learn",
        title: "What Is Dementia? A Plain-English Guide for Caregivers",
        meta: "Dementia explained simply for family caregivers — symptoms, types, what's NOT dementia, and the very first things to do after a diagnosis.",
        h1: "What is dementia?",
        summary: "Dementia is an umbrella term for a decline in memory, thinking, or behavior serious enough to interfere with daily life. It is not a normal part of aging.",
        body: [
            { type: "h2", text: "It's a syndrome, not a disease" },
            { type: "p", text: "\"Dementia\" describes a set of symptoms — losing track of words, getting lost in familiar places, missing appointments, struggling with money. Several different diseases can cause those symptoms, and the cause matters because each one progresses differently and may respond to different medications." },
            { type: "h2", text: "The most common causes" },
            { type: "ul", items: [
                "Alzheimer's disease — about 60–70% of cases. Memory loss is usually the earliest sign.",
                "Vascular dementia — caused by small strokes. Often stair-step progression.",
                "Lewy body dementia — visual hallucinations, sleep disturbance, and Parkinson-like stiffness early on.",
                "Frontotemporal dementia — personality, judgement, or language changes before memory.",
                "Mixed dementia — most older brains show more than one cause.",
            ]},
            { type: "h2", text: "What dementia is NOT" },
            { type: "ul", items: [
                "Normal aging. Forgetting names is common; forgetting how to use a fork is not.",
                "Depression — but depression in older adults can look like dementia and is treatable.",
                "Medication side effects — anticholinergics, benzodiazepines, opioids can all mimic dementia.",
                "Thyroid, B12, or urinary tract infection — any of these can cause confusion overnight.",
            ]},
            { type: "callout", tone: "teal", text: "If your loved one's confusion came on in days or weeks (not months or years), please rule out delirium FIRST with their doctor before anyone calls it dementia." },
            { type: "h2", text: "The first 30 days after a diagnosis" },
            { type: "ol", items: [
                "Confirm the type — ask the doctor which form of dementia they suspect, and on what evidence.",
                "Power of attorney while your loved one can still sign. Don't wait.",
                "Update advance directives — healthcare proxy, living will, POLST.",
                "Audit medications with a pharmacist — many older adults take meds that worsen confusion.",
                "Check eligibility for the Medicare GUIDE program — a navigator, 24/7 helpline, and up to $2,500/year of respite at no cost.",
            ]},
        ],
        faq: [
            { q: "Is forgetting names a sign of dementia?", a: "Occasionally forgetting a name and remembering it later is normal. Forgetting common words or losing track of conversations regularly is worth a doctor's appointment." },
            { q: "Can dementia be reversed?", a: "Some causes — vitamin deficiency, thyroid disease, medication side effects, depression, normal-pressure hydrocephalus — can be reversed or substantially improved. Alzheimer's and most other neurodegenerative dementias cannot be reversed but can be slowed and managed." },
            { q: "How long does dementia last?", a: "Average life expectancy from diagnosis is 8–10 years for Alzheimer's, but ranges widely. Lewy body and frontotemporal forms often progress faster." },
        ],
        related: ["stages-of-dementia", "alzheimers-vs-dementia", "sundowning", "stop-driving", "guide-eligibility"],
        lane_prompt: "Help me understand what dementia is and what to do first after my loved one's diagnosis.",
        updated_at: "2026-02-15",
    },
    {
        slug: "stages-of-dementia",
        group: "learn",
        title: "The 7 Stages of Dementia, Explained for Families",
        meta: "Mild, moderate, severe — what to expect at each stage of dementia and what caregivers can do right now. Includes early-warning signs and end-of-life signals.",
        h1: "The stages of dementia",
        summary: "Dementia is progressive — symptoms worsen over time — but the path is never identical between people. Knowing the stages helps you plan, not predict.",
        body: [
            { type: "h2", text: "Three stages, seven sub-stages" },
            { type: "p", text: "Clinicians usually talk about three stages — mild, moderate, severe. The Global Deterioration Scale (GDS) splits them into seven. The big picture is what matters for families." },
            { type: "h2", text: "Early stage (mild)" },
            { type: "ul", items: [
                "Repeats stories or questions within minutes",
                "Loses track of bills, missed appointments, misplaced items",
                "Word-finding pauses; withdraws from group conversations",
                "Driving errors begin — but they can still mostly compensate",
                "Independent for now, but a calendar, pill organizer, and weekly check-in help",
            ]},
            { type: "h2", text: "Middle stage (moderate)" },
            { type: "ul", items: [
                "Needs help with dressing, bathing, choosing clothes",
                "Sundowning — late-afternoon agitation, pacing, wanting to \"go home\"",
                "Wandering — even from a familiar yard. Door alarms and GPS help.",
                "Sleep flips — up at night, drowsy by day",
                "May not recognize close family at moments — still recognizes warmth",
            ]},
            { type: "h2", text: "Late stage (severe)" },
            { type: "ul", items: [
                "Needs full help with everything — toileting, feeding, transfers",
                "Communication is mostly nonverbal; touch and music still reach",
                "Weight loss, swallowing problems, recurrent infections",
                "Hospice is often appropriate — focus shifts to comfort",
            ]},
            { type: "callout", tone: "coral", text: "Stages don't move on a schedule. A person can hover in the middle for years, or decline rapidly after a hospitalization. Plan for what's likely; don't grieve what hasn't happened yet." },
        ],
        faq: [
            { q: "How fast does dementia progress?", a: "Average decline is gradual over 8–10 years, but stress, infection, surgery, or a move can cause a sudden step down. Many caregivers describe \"plateaus\" punctuated by drops." },
            { q: "What stage qualifies for hospice?", a: "In the US, hospice is appropriate when life expectancy is six months or less if the disease runs its course — typically late stage with weight loss, recurrent infection, and inability to walk or speak meaningfully." },
            { q: "What stage qualifies for memory care?", a: "Most families consider memory care in the middle stage when safety, wandering, or 24/7 supervision needs exceed what they can sustain at home." },
        ],
        related: ["what-is-dementia", "sundowning", "wandering", "guide-eligibility", "moving-to-memory-care"],
        lane_prompt: "What stage of dementia is my loved one in, and what should I be doing now to prepare for the next stage?",
        updated_at: "2026-02-15",
    },
    {
        slug: "alzheimers-vs-dementia",
        group: "learn",
        title: "Alzheimer's vs Dementia: What's the Difference?",
        meta: "Dementia is the umbrella; Alzheimer's is the most common cause. The plain-English difference, why the diagnosis matters, and what to ask the neurologist.",
        h1: "Alzheimer's vs dementia — what's the difference?",
        summary: "Dementia describes the symptoms. Alzheimer's is one specific disease that causes them — the most common one, but not the only one.",
        body: [
            { type: "h2", text: "Two questions, not one" },
            { type: "p", text: "When the neurologist says \"dementia,\" the next question they should answer is \"what kind?\" The type matters because Alzheimer's, vascular, Lewy body, and frontotemporal dementia each progress differently and respond to different treatments — sometimes opposite treatments." },
            { type: "h2", text: "Why the cause matters" },
            { type: "ul", items: [
                "Alzheimer's responds to cholinesterase inhibitors (donepezil, rivastigmine) and the newer amyloid-clearing infusions (lecanemab, donanemab).",
                "Lewy body dementia can be made dramatically worse by typical antipsychotics — Haldol, in particular, can be dangerous.",
                "Vascular dementia benefits most from blood-pressure and stroke-prevention care.",
                "Frontotemporal dementia rarely responds to Alzheimer's drugs — and they may worsen behaviour.",
            ]},
            { type: "callout", tone: "teal", text: "Ask the doctor: \"Which form of dementia do you think this is, and on what evidence?\" A reasonable answer involves imaging (MRI), labs, and sometimes a PET scan or spinal fluid biomarker." },
        ],
        faq: [
            { q: "Can someone have both Alzheimer's and another type?", a: "Yes — \"mixed dementia\" is common, especially Alzheimer's + vascular. Autopsy studies show most people over 80 with dementia have more than one pathology." },
            { q: "Is the diagnosis ever wrong?", a: "Yes — early-stage misdiagnosis happens. If something doesn't fit (e.g. hallucinations, sleep behavior, asymmetric symptoms), a second opinion is worth asking for." },
        ],
        related: ["what-is-dementia", "stages-of-dementia", "lecanemab-leqembi", "donepezil-aricept"],
        lane_prompt: "Help me understand what kind of dementia my loved one has and what questions to ask the neurologist.",
        updated_at: "2026-02-15",
    },

    // ===== BEHAVIOR GUIDANCE =====
    {
        slug: "sundowning",
        group: "behavior",
        title: "Sundowning in Dementia: What It Is and What Helps Tonight",
        meta: "Sundowning explained — why late-afternoon agitation happens in dementia, what works tonight, and what NOT to do. Plain steps for caregivers.",
        h1: "Sundowning — why afternoons get hard, and what helps",
        summary: "Sundowning is the cluster of agitation, restlessness, confusion or anxiety that often shows up in late afternoon and early evening. It's exhausting — and it's manageable.",
        body: [
            { type: "h2", text: "What's actually happening" },
            { type: "p", text: "By late afternoon, the brain is tired. The internal body clock is wobbly in dementia. Add fading light, hunger, a full bladder, and a day of effort — and you get agitation that can look like a different person walked in the door." },
            { type: "h2", text: "What to try first (tonight)" },
            { type: "ol", items: [
                "Light. Turn on every overhead and lamp 30 minutes BEFORE sundown. Close blinds against fading-light shadows.",
                "Hunger and toileting. A small snack and a bathroom trip before agitation starts.",
                "Calm input. Soft music your loved one knew at 25–40 years old. No news, no shouting kids on a tablet.",
                "Lower the demand. Stop trying to teach, correct, or finish tasks. Just be next to them.",
                "Walk. A 10-minute slow walk often resets agitation better than any drug.",
            ]},
            { type: "h2", text: "What NOT to do" },
            { type: "ul", items: [
                "Don't argue with the false belief (\"I need to pick up the kids\"). Validate the feeling, redirect the activity.",
                "Don't reach for a benzodiazepine (Ativan, Xanax) as a first move — they often worsen confusion and falls.",
                "Don't take agitation personally. The behavior is a symptom, not directed at you.",
            ]},
            { type: "callout", tone: "coral", text: "If sundowning suddenly worsens within a few days, suspect infection — especially urinary tract infection — or a new medication. Both are extremely common triggers." },
        ],
        faq: [
            { q: "What time of day is sundowning?", a: "Typically 3 PM to 8 PM, sometimes lasting into the night. The first sign is often subtle — restlessness, pacing, wanting to leave." },
            { q: "Does melatonin help with sundowning?", a: "Modest evidence at low dose (0.5–3 mg) about 30 minutes before the usual agitation. Talk to the prescriber — it interacts with some medications." },
            { q: "When should we call the doctor?", a: "If agitation is new, severe, or comes with confusion plus fever, pain, or a fall — same day. If it's been creeping up over weeks — at the next visit." },
        ],
        related: ["wandering", "aggression", "sleep-disturbance", "refusing-care", "hallucinations"],
        lane_prompt: "My loved one gets agitated every afternoon — what should I try tonight?",
        updated_at: "2026-02-15",
    },
    {
        slug: "wandering",
        group: "behavior",
        title: "Wandering in Dementia: How to Keep Them Safe at Home",
        meta: "Why people with dementia wander, how to prevent it, what to do if they leave, and the GPS + door-alarm tools that actually work.",
        h1: "Wandering — keeping the door, the car, and the night safe",
        summary: "Six in ten people with dementia will wander at some point. Plan before it happens — recovery is almost always about minutes, not hours.",
        body: [
            { type: "h2", text: "Why people wander" },
            { type: "ul", items: [
                "They believe they're somewhere else (\"I have to get home,\" though they're already home).",
                "They're looking for someone — often a parent who passed years ago.",
                "Restlessness — pacing builds up, the door is the next step.",
                "Pain, full bladder, hunger — wandering is sometimes a request without words.",
            ]},
            { type: "h2", text: "Prevention layers" },
            { type: "ol", items: [
                "Door alarms — simple battery alarms on every exterior door and the door to the garage.",
                "Hidden bolts at the top OR bottom of the door — most people with dementia don't look up or down.",
                "A \"camouflage\" curtain or a STOP sign on the inside of the front door.",
                "ID — a MedicAlert + Alzheimer's Association Wandering Support bracelet ($35, includes 24/7 emergency response).",
                "GPS — Apple AirTag in their wallet, Jiobit for a child-style tracker, or a GPS smartwatch they will actually wear.",
            ]},
            { type: "h2", text: "If they leave" },
            { type: "ol", items: [
                "Call 911 immediately — don't wait the old \"24 hours\" myth. Police treat dementia wandering as a critical missing person.",
                "Check water first — 75% of people who wander are found within a half mile, often near water.",
                "Have a current photo on your phone and a printed sheet of identifying details ready to hand to police.",
            ]},
            { type: "callout", tone: "teal", text: "Save Smart911 (or your country's equivalent) before you need it. It lets 911 dispatchers see your loved one's dementia diagnosis and photo the moment a call comes in." },
        ],
        faq: [
            { q: "Should I lock them in the house?", a: "Never to the point where they cannot exit in a fire. Bolts and alarms that need adult-level dexterity are the safer balance." },
            { q: "Will a GPS bracelet help?", a: "Only if your loved one keeps it on. Some refuse — try a familiar wristwatch with a hidden tracker, or a tracker sewn into a belt or shoe." },
        ],
        related: ["sundowning", "stop-driving", "moving-to-memory-care"],
        lane_prompt: "I'm scared my loved one will wander out at night. What's the layered plan to keep them safe?",
        updated_at: "2026-02-15",
    },
    {
        slug: "refusing-care",
        group: "behavior",
        title: "When a Person with Dementia Refuses Care",
        meta: "Bathing, dressing, taking meds — what to do when your loved one refuses, how to avoid making it worse, and the techniques that work.",
        h1: "Refusing care — what to do when bathing or meds become a battle",
        summary: "Refusal is rarely about the task. It's about feeling cold, exposed, embarrassed, or out of control. The path through is rarely arguing.",
        body: [
            { type: "h2", text: "Before the refusal — set it up to win" },
            { type: "ul", items: [
                "Warm the room and the towels. Cold air is a refusal magnifier.",
                "Same time of day every time — routine reduces resistance.",
                "Same caregiver where possible. Strangers double the risk of refusal.",
                "Soft music, dim lights, calm voice. Quiet wins over efficient.",
                "Offer two choices, never an open question — \"navy blue or grey shirt?\" not \"want to get dressed?\"",
            ]},
            { type: "h2", text: "In the moment" },
            { type: "ol", items: [
                "Drop the demand. Walk away for 10 minutes. Tea. Music. Come back fresh.",
                "Change WHO is asking. Sometimes a different voice flips it.",
                "Validate the feeling — \"I know you don't want to. Let's do just your face today.\"",
                "Break it down. Sponge bath instead of shower. Hair tomorrow. Done is better than perfect.",
            ]},
            { type: "callout", tone: "coral", text: "If you find yourself wrestling them, restraining, or shouting — STOP. You are exhausted. Walk away. Nothing about this task is worth either of you getting hurt." },
        ],
        faq: [
            { q: "Should I hide medication in food?", a: "Sometimes. Crushing pills changes the dosage of some meds (sustained-release, enteric-coated). Ask the pharmacist before you start." },
            { q: "What if they refuse for days?", a: "After 2–3 days of refused bathing, expose only what needs cleaning — perineum, underarms, hands. Skip the rest. Their dignity matters more than a full shower." },
        ],
        related: ["sundowning", "aggression", "hallucinations"],
        lane_prompt: "My loved one refuses to bathe — give me 5 things to try this week.",
        updated_at: "2026-02-15",
    },

    // ===== GUIDE =====
    {
        slug: "guide-eligibility",
        group: "guide",
        title: "Medicare GUIDE: Who's Eligible and How to Sign Up",
        meta: "GUIDE is Medicare's dementia care navigation program. Eligibility, what's covered (incl. $2,500/yr respite), and how to find a participating provider.",
        h1: "Am I eligible for Medicare GUIDE?",
        summary: "GUIDE (Guiding an Improved Dementia Experience) is a Medicare program launched in 2024 that gives families a care navigator, 24/7 helpline, caregiver training, and up to $2,500/year in respite — all at no copay.",
        body: [
            { type: "h2", text: "Eligibility — the four things" },
            { type: "ol", items: [
                "A confirmed dementia diagnosis (Alzheimer's, vascular, Lewy body, frontotemporal, or mixed).",
                "Enrolled in traditional Medicare — Part A AND Part B. (Medicare Advantage members are NOT eligible.)",
                "Living in the community — not in a nursing home or long-term-care facility.",
                "Not enrolled in another care navigation program like PACE or hospice.",
            ]},
            { type: "h2", text: "What GUIDE includes" },
            { type: "ul", items: [
                "A dedicated care navigator who coordinates appointments, meds, and the whole care team.",
                "24/7 nurse helpline — call any night for what to do at 2 AM.",
                "Up to $2,500/year of respite care so the caregiver can rest.",
                "Caregiver training — sundowning, refusing care, transitions.",
                "Behavioral health support for the person with dementia AND the caregiver.",
            ]},
            { type: "callout", tone: "teal", text: "If you're on a Medicare Advantage plan and want GUIDE, you can switch back to traditional Medicare during the Annual Election Period (Oct 15–Dec 7) or special-enrollment windows." },
            { type: "h2", text: "Finding a participating provider" },
            { type: "p", text: "GUIDE is delivered through participating health systems and clinics. Memory Lane's Resources page filters them by your state — pick the GUIDE chip on /help-finder to see who's participating near you. You can also search the CMS GUIDE Model directory directly." },
        ],
        faq: [
            { q: "Does GUIDE cost anything?", a: "No copay for GUIDE services. You keep your regular Medicare premiums and any cost-sharing for non-GUIDE care." },
            { q: "Can I get GUIDE if my parent already has a neurologist?", a: "Yes — GUIDE complements the neurologist. Your navigator coordinates with them rather than replacing them." },
            { q: "Is GUIDE the same as PACE?", a: "No. PACE is a comprehensive program that covers all medical care; GUIDE is a navigation + caregiver-support overlay on top of regular Medicare." },
        ],
        related: ["what-is-dementia", "stages-of-dementia", "finding-respite", "va-aid-and-attendance"],
        lane_prompt: "Help me figure out if my mom qualifies for Medicare GUIDE and how to sign up.",
        updated_at: "2026-02-15",
    },

    // ===== PAYING FOR CARE =====
    {
        slug: "va-aid-and-attendance",
        group: "paying-for-care",
        title: "VA Aid & Attendance: Up to $30,000/yr for Dementia Caregivers",
        meta: "How VA Aid and Attendance works for veterans and surviving spouses, current 2026 benefit amounts, who qualifies, and how to apply.",
        h1: "VA Aid and Attendance — the most-missed benefit for dementia families",
        summary: "Aid and Attendance is a tax-free monthly benefit on top of the VA pension. For a veteran with a spouse needing care, it can run over $2,800/month — and most families don't know it exists.",
        body: [
            { type: "h2", text: "Who qualifies" },
            { type: "ul", items: [
                "A veteran with 90+ days of active duty, at least one day during a wartime period (WWII, Korea, Vietnam, Gulf War, post-9/11).",
                "Or a surviving spouse of such a veteran (must not have remarried).",
                "Needs help with at least two activities of daily living (bathing, dressing, eating, transferring, toileting, continence).",
                "Below the VA's income + asset thresholds (the bar is generous when medical expenses count against income).",
            ]},
            { type: "h2", text: "2026 maximum benefit (tax-free, monthly)" },
            { type: "ul", items: [
                "Single veteran: ~$2,358",
                "Veteran with one dependent: ~$2,795",
                "Surviving spouse: ~$1,515",
                "Two veterans married to each other: ~$3,740",
            ]},
            { type: "callout", tone: "teal", text: "Caregiver costs — including paying a family member for documented in-home care hours — count as medical expenses that reduce your countable income for eligibility." },
            { type: "h2", text: "How to apply" },
            { type: "ol", items: [
                "Use VA Form 21-2680 (Examination for Housebound Status or Permanent Need for Regular Aid and Attendance) — your doctor completes the medical sections.",
                "File alongside VA Form 21P-527EZ (pension) or 21P-534EZ (survivors).",
                "Use a Veterans Service Organization (VFW, American Legion, Disabled American Veterans) — they file for free and know the rules.",
                "Never pay a \"pension consultant\" for help — it's illegal under VA rules.",
            ]},
        ],
        faq: [
            { q: "How long does approval take?", a: "Currently 4–7 months. Backdated to the application date once approved." },
            { q: "Does the veteran have to be in a facility?", a: "No. Aid and Attendance can pay for in-home care, assisted living, or memory care — wherever the documented need is being met." },
            { q: "Does it affect Medicaid eligibility?", a: "It can. Aid and Attendance counts as income for Medicaid. Talk to an elder-law attorney before applying if you might need Medicaid later." },
        ],
        related: ["medicare-coverage", "medicaid-waivers", "ltc-insurance", "guide-eligibility"],
        lane_prompt: "Walk me through whether my dad qualifies for VA Aid and Attendance and how to apply.",
        updated_at: "2026-02-15",
    },
    {
        slug: "medicare-coverage",
        group: "paying-for-care",
        title: "What Medicare Covers for Dementia Care — and What It Doesn't",
        meta: "Plain-English breakdown of Medicare for dementia families: what Part A, B, D, and the GUIDE benefit pay for, and what you'll pay out of pocket.",
        h1: "What Medicare covers for dementia care",
        summary: "Medicare covers a lot of medical care — and almost no long-term care. Knowing the line saves families thousands.",
        body: [
            { type: "h2", text: "What Medicare DOES cover" },
            { type: "ul", items: [
                "Doctor visits, neurology, geriatrics, psychiatry (Part B).",
                "Hospital stays and short-term skilled nursing rehab after a 3-day inpatient stay (Part A — up to 100 days, with copays starting day 21).",
                "Home health — skilled nursing, PT, OT, speech, social work — if homebound and a doctor orders it (Part A/B).",
                "Hospice — when prognosis is ≤6 months (Part A, no copay).",
                "Most medications (Part D, with copays).",
                "GUIDE navigation, caregiver training, and respite (no copay, traditional Medicare only).",
            ]},
            { type: "h2", text: "What Medicare does NOT cover" },
            { type: "ul", items: [
                "Long-term care at home or in a facility (this is the big one — it's why families burn through savings).",
                "Custodial care — help with bathing, dressing, supervision — unless it comes alongside skilled care.",
                "Adult day programs (some Medicare Advantage plans now offer them as a supplemental benefit — check your plan).",
                "Most assisted living and memory care.",
            ]},
            { type: "callout", tone: "coral", text: "The hardest line for families: Medicare doesn't pay for someone to sit with your loved one while you go to work. That's custodial care. Look at GUIDE respite, Medicaid waivers, VA Aid and Attendance, and LTC insurance for that gap." },
        ],
        faq: [
            { q: "Does Medicare cover Lecanemab or Donanemab?", a: "Yes — Medicare Part B covers FDA-approved amyloid-clearing drugs for eligible patients. Required: amyloid biomarker confirmation, ongoing MRI safety monitoring, and a participating infusion site." },
            { q: "Does Medicare cover memory care assisted living?", a: "No. Memory care assisted living is room, board, and custodial care — none of which Medicare pays for. Some residents use VA Aid and Attendance, LTC insurance, or Medicaid waivers." },
        ],
        related: ["medicaid-waivers", "va-aid-and-attendance", "guide-eligibility", "lecanemab-leqembi"],
        lane_prompt: "What does Medicare pay for in my mom's dementia journey, and what's NOT covered?",
        updated_at: "2026-02-15",
    },

    // ===== RESOURCES =====
    {
        slug: "home-health-vs-hospice",
        group: "resources",
        title: "Home Health vs Hospice: Plain-English Differences",
        meta: "Home health is for getting better; hospice is for comfort. Side-by-side: who qualifies, what's covered, what's NOT, and how to choose for someone with dementia.",
        h1: "Home health vs hospice — they sound similar; they're not",
        summary: "Families confuse these two all the time, and the difference shapes everything from cost to care intensity to what gets covered.",
        body: [
            { type: "h2", text: "Home health" },
            { type: "ul", items: [
                "Goal: improve or maintain function — wound care, physical therapy after surgery, IV antibiotics at home.",
                "Eligibility: Medicare-certified, doctor's order, homebound, intermittent skilled need.",
                "Frequency: a nurse 1–3 visits/week for weeks, not months.",
                "Cost: Medicare Part A or B, no copay.",
            ]},
            { type: "h2", text: "Hospice" },
            { type: "ul", items: [
                "Goal: comfort. Symptom control, dignity, family support.",
                "Eligibility: two physicians certify prognosis is ≤6 months if the disease runs its course.",
                "Frequency: nurse 1–3 visits/week, aide several times/week, on-call 24/7, chaplain, social worker, volunteer.",
                "Cost: Medicare Hospice Benefit covers nearly everything, including medications related to the terminal illness, equipment, and most of inpatient respite.",
            ]},
            { type: "callout", tone: "teal", text: "You can have BOTH at different times — home health for a knee replacement now, hospice later when comfort is the goal. They don't overlap on the same diagnosis." },
            { type: "h2", text: "Choosing for dementia" },
            { type: "p", text: "In late-stage dementia, hospice is often more appropriate than home health. Look for: significant weight loss, recurrent infections, inability to walk or speak meaningfully. Two hospice clinicians can determine eligibility." },
        ],
        faq: [
            { q: "Can a dementia patient be on hospice for over a year?", a: "Yes. The six-month estimate is a guideline; many dementia patients are recertified beyond it." },
            { q: "Does the hospice nurse live with us?", a: "No. Hospice is intermittent visits, not 24/7 in-home care. Family or paid caregivers still provide most of the hands-on care." },
        ],
        related: ["choosing-adult-day-program", "finding-respite", "what-is-dementia", "stages-of-dementia"],
        lane_prompt: "Help me figure out if my dad needs home health or hospice right now.",
        updated_at: "2026-02-15",
    },
    {
        slug: "finding-respite",
        group: "resources",
        title: "Finding Respite Care for Dementia — Free, Low-Cost, and Paid",
        meta: "Respite care options for dementia caregivers — Medicaid waivers, VA, GUIDE's $2,500/yr benefit, Alzheimer's Association grants, and in-home matchmaking.",
        h1: "Where to find respite care that families actually use",
        summary: "Respite isn't a luxury — it's how caregivers stay alive long enough to keep caregiving. Here's where the money is.",
        body: [
            { type: "h2", text: "Funded respite options" },
            { type: "ol", items: [
                "Medicare GUIDE — up to $2,500/year of respite for eligible families. Zero copay. Talk to your GUIDE navigator.",
                "VA Aid and Attendance — covers in-home help for veterans and surviving spouses.",
                "Medicaid Home and Community-Based Services (HCBS) waivers — vary by state, sometimes pay a family member.",
                "Alzheimer's Foundation of America grants — up to $500 for emergency respite.",
                "Hospice respite benefit — Medicare covers up to 5 consecutive days in a facility, can be used multiple times per benefit period.",
            ]},
            { type: "h2", text: "Where to actually use it" },
            { type: "ul", items: [
                "Adult day programs — social, meals, light supervision. ~$80/day in most states.",
                "In-home aides — companion care from agencies or matched directly (Memory Lane's Care Network can help here).",
                "Overnight respite at memory care — book a weekend or a week.",
                "Specialized respite camps (Lorenzo's House, Alzheimer's Association support — varies by region).",
            ]},
            { type: "callout", tone: "coral", text: "If you're white-knuckling it through every weekend — please don't wait for a crisis. Burned-out caregivers end up in the ER themselves. Respite is preventive medicine for YOU." },
        ],
        faq: [
            { q: "Can my sister get paid to help with mom?", a: "In many states, yes — through Medicaid HCBS waivers, the Consumer Directed Care option lets the family member act as the paid caregiver. Rules vary by state." },
            { q: "What if my loved one refuses respite?", a: "Start very small — an hour. Pair it with their favorite music, food, or visitor. Most refusal melts once they realize it's a break that's also a social outing." },
        ],
        related: ["va-aid-and-attendance", "medicaid-waivers", "guide-eligibility", "moving-to-memory-care"],
        lane_prompt: "Help me find respite care for my mom this week — I haven't slept properly in months.",
        updated_at: "2026-02-15",
    },

    // ===== TREATMENTS =====
    {
        slug: "lecanemab-leqembi",
        group: "treatments",
        title: "Lecanemab (Leqembi): What Families Need to Know",
        meta: "Leqembi explained for caregivers — how it works, who qualifies, how much it slows decline, infusion logistics, side effects, and Medicare coverage.",
        h1: "Lecanemab (Leqembi)",
        summary: "Lecanemab is one of the first FDA-approved infusions that clears amyloid plaques from the brain. It's not a cure — it slows decline modestly in early Alzheimer's.",
        body: [
            { type: "h2", text: "How it works" },
            { type: "p", text: "Lecanemab is a monoclonal antibody. Given by IV every two weeks, it sticks to amyloid plaques and signals the immune system to clear them. Clinical trials showed roughly 27% slower decline over 18 months in early-stage Alzheimer's." },
            { type: "h2", text: "Who qualifies" },
            { type: "ul", items: [
                "Confirmed early-stage Alzheimer's (mild cognitive impairment or mild dementia).",
                "Amyloid biomarker positive — either PET scan or cerebrospinal fluid result.",
                "No history of recent stroke, brain bleed, or active anticoagulation in some clinics.",
                "Able to attend infusions every two weeks and tolerate frequent MRIs.",
            ]},
            { type: "h2", text: "Side effects to know" },
            { type: "ul", items: [
                "ARIA — amyloid-related imaging abnormalities. Roughly 12–17% of patients show brain swelling or microbleeds on MRI. Most cases are mild and symptom-free; some are serious.",
                "Higher risk for people with two copies of the APOE-ε4 gene.",
                "Infusion reactions — chills, nausea, headache — usually first dose.",
            ]},
            { type: "callout", tone: "coral", text: "Lecanemab requires ongoing MRI monitoring (typically at 5, 7, 14, and 26 infusions). Skipping safety MRIs is not safe." },
        ],
        faq: [
            { q: "Does Medicare cover Leqembi?", a: "Yes — Medicare Part B covers it for eligible patients with amyloid confirmation and ongoing safety monitoring." },
            { q: "Is Donanemab the same?", a: "Different drug, same class (anti-amyloid). Donanemab (Kisunla) is monthly and stops once amyloid is cleared. Roughly similar slowing of decline." },
            { q: "Will my mom recognize me longer if she takes it?", a: "Possibly — the slower decline is real but modest. Most families report subtle stability rather than dramatic improvement." },
        ],
        related: ["donanemab-kisunla", "donepezil-aricept", "what-is-dementia", "alzheimers-vs-dementia"],
        lane_prompt: "Should we ask my mom's neurologist about Leqembi? What questions should I ask?",
        updated_at: "2026-02-15",
    },
    {
        slug: "donepezil-aricept",
        group: "treatments",
        title: "Donepezil (Aricept): What It Does and What It Doesn't",
        meta: "Donepezil for dementia explained — when it helps, how to start it, side effects to watch for, and when to stop. Caregiver-focused.",
        h1: "Donepezil (Aricept)",
        summary: "Donepezil is the most prescribed dementia medication in the world. It doesn't cure, doesn't stop progression, but in many people it sharpens day-to-day function modestly.",
        body: [
            { type: "h2", text: "What it does" },
            { type: "p", text: "Donepezil boosts acetylcholine in the brain — a chemical messenger that's depleted in Alzheimer's and Lewy body dementia. Most patients see no dramatic change. Some get a noticeable lift in word-finding, attention, or daily organization within 6–12 weeks." },
            { type: "h2", text: "Starting and titrating" },
            { type: "ul", items: [
                "Typical start: 5 mg at bedtime for 4–6 weeks.",
                "Then increase to 10 mg if tolerated.",
                "Some patients move to 23 mg later, though benefit is debated.",
            ]},
            { type: "h2", text: "Side effects to watch for" },
            { type: "ul", items: [
                "Nausea, diarrhea, loss of appetite (the most common, usually mild).",
                "Vivid dreams or nightmares — taking it in the morning instead of bedtime can help.",
                "Slow heart rate — important if your loved one is on heart-rhythm medications.",
                "Muscle cramps, restless legs.",
            ]},
            { type: "callout", tone: "teal", text: "Stopping donepezil suddenly often leads to a noticeable decline within weeks. If the prescriber wants to stop it, taper rather than quitting cold." },
        ],
        faq: [
            { q: "Does donepezil work for all dementias?", a: "Best evidence in Alzheimer's and Lewy body. Less helpful in pure vascular dementia. Not used in frontotemporal." },
            { q: "How will we know if it's working?", a: "Pick 2–3 specific things to watch before starting — names of grandchildren, finishing a familiar task, mood. Check them at 8 and 12 weeks." },
            { q: "When should we stop it?", a: "When side effects outweigh benefit, or in very late-stage dementia when comfort is the only goal." },
        ],
        related: ["lecanemab-leqembi", "donanemab-kisunla", "what-is-dementia"],
        lane_prompt: "My dad just started Aricept — what should we watch for in the first three months?",
        updated_at: "2026-02-15",
    },

    // ===== HARD CONVERSATIONS =====
    {
        slug: "stop-driving",
        group: "hard-conversations",
        title: "When and How to Stop a Loved One with Dementia from Driving",
        meta: "Driving is often the hardest conversation. How to recognize when it's time, how to test it safely, and how to take the keys without losing the relationship.",
        h1: "When it's time to stop driving",
        summary: "Driving is identity, freedom, dignity. It is also two tons of metal at 50 mph. Most dementia families do not get this conversation right the first time, and that's OK.",
        body: [
            { type: "h2", text: "Warning signs it's time" },
            { type: "ul", items: [
                "New dents and scrapes on the car they can't explain.",
                "Getting lost in familiar places (the corner grocery store, the way home from church).",
                "Confusing the gas and the brake — even once.",
                "Slow to react at lights or stop signs; friends or family report \"a close call.\"",
                "Increasing anxiety about driving — often the first to know is the person themselves.",
            ]},
            { type: "h2", text: "Before you take the keys" },
            { type: "ol", items: [
                "Ask the doctor to recommend a driving evaluation — most states have an Occupational Therapy driving program. An outside expert removes the family blame.",
                "Use the AAA self-assessment together — sometimes the data does the talking.",
                "If they want to drive themselves to the evaluation, that's the safer first step.",
            ]},
            { type: "h2", text: "Taking the keys without losing the relationship" },
            { type: "ul", items: [
                "Don't lie about \"the car is broken\" forever — they may notice. Frame it as the doctor's recommendation.",
                "Replace the freedom — Uber Health, Lyft, GoGoGrandparent, or a volunteer driver program — BEFORE you take the keys.",
                "Plan for the grief. Most people experience real loss when they stop driving. Don't dismiss it.",
                "Disable the car if needed — battery cable removed by a mechanic, keys hidden, or sell it.",
            ]},
            { type: "callout", tone: "coral", text: "If your loved one is actively unsafe right now and refusing — call their doctor TODAY. Most states allow physicians to report unsafe drivers to the DMV, which is often the kinder external authority families need." },
        ],
        faq: [
            { q: "Is it legal to take their keys?", a: "Yes — but the cleaner path is a driving evaluation, doctor's letter, or DMV-required retest. It keeps the conflict between them and an external authority, not between you and them." },
            { q: "What if they have a fender bender?", a: "Treat it as the conversation. \"This was lucky — next time could be worse. Let's do an evaluation.\"" },
        ],
        related: ["wandering", "sharing-the-diagnosis", "moving-to-memory-care"],
        lane_prompt: "How do I have the driving conversation with my mom without destroying our relationship?",
        updated_at: "2026-02-15",
    },
    {
        slug: "sharing-the-diagnosis",
        group: "hard-conversations",
        title: "Should You Tell Your Loved One They Have Dementia?",
        meta: "Sharing the dementia diagnosis with the person who has it — when it helps, when it hurts, and the words families have used that worked.",
        h1: "Should we tell mom she has dementia?",
        summary: "There isn't one right answer. Most experts now recommend telling, gently and once, in a way that gives them agency. Some families choose not to. Both can be right.",
        body: [
            { type: "h2", text: "Why telling usually helps" },
            { type: "ul", items: [
                "It lets them grieve, plan, and say things while they still can.",
                "It opens the door to advance directives, power of attorney, end-of-life wishes.",
                "It removes the lonely fear of \"what's wrong with me?\"",
                "It often relieves them — they already knew something was off.",
            ]},
            { type: "h2", text: "When less detail is kinder" },
            { type: "ul", items: [
                "Late-stage — they can't hold the information; telling them daily is cruel.",
                "Severe anxiety or paranoia about the diagnosis specifically.",
                "When the question itself is the symptom — \"is this dementia?\" said hourly is a different conversation.",
            ]},
            { type: "h2", text: "Words that have worked" },
            { type: "callout", tone: "teal", text: "\"The doctor explained your memory is not working the way it used to. There's a name for it — it's called dementia. It doesn't change who you are. We're going to keep going together, and we're going to plan some things while you're feeling clear so you stay in charge of what matters to you.\"" },
        ],
        faq: [
            { q: "What if they forget the conversation?", a: "Most do. The act of being told often still settles something. You can return to the conversation at less detail later — \"the doctor told us your memory is slipping a bit; we're going to make some plans.\"" },
            { q: "Should we tell young grandchildren?", a: "Yes, at their level. Children handle truth better than secrecy. \"Grandma's brain is sick. She might forget your name. Hugs still work.\"" },
        ],
        related: ["stop-driving", "end-of-life-wishes", "what-is-dementia"],
        lane_prompt: "We just got a dementia diagnosis. Help me figure out if and how to tell mom.",
        updated_at: "2026-02-15",
    },

    // ===== BATCH 2 — additional behavior, GUIDE, paying-for-care, treatments,
    //                 hard-conversations, learn, resources (iter36) =====
    {
        slug: "agitation",
        group: "behavior",
        title: "Agitation in Dementia — Why It Happens & What Calms It",
        meta: "Agitation in dementia explained — common triggers (pain, infection, overstimulation), what to try first, and when to involve a doctor. For family caregivers.",
        h1: "Agitation — what's underneath, and what calms it",
        summary: "Agitation in dementia is usually a signal of unmet need — pain, hunger, full bladder, fear — that the person can't put into words. The path through is detective work, not arguing.",
        body: [
            { type: "h2", text: "Common triggers to rule out first" },
            { type: "ul", items: ["Pain — urinary tract infection is the #1 missed cause of new agitation.", "Constipation — bowel discomfort flares behavior dramatically.", "Hunger or thirst.", "Too much noise / too many people / a busy environment.", "A new medication or recent change to dose.", "Sleep loss — overtired adults regress like overtired kids."] },
            { type: "h2", text: "Calming techniques that work" },
            { type: "ol", items: ["Lower the demand. Stop the task. Sit down. Match their breath.", "Music they knew at 25–40 years old.", "Move to a smaller, quieter room. Dim the lights.", "Hand massage with warm lotion — touch reaches when words don't.", "A familiar object — wallet, watch, photo album."] },
            { type: "callout", tone: "coral", text: "New or worsening agitation over a few days is a medical event. Call the doctor and ask about a urine check, pain assessment, and medication review before anyone reaches for an antipsychotic." },
        ],
        faq: [
            { q: "Are antipsychotics ever the right answer?", a: "Sometimes, in narrow cases, prescribed carefully — but they carry stroke and death risk in dementia. Try environmental + medical workups first." },
            { q: "Will Lewy body dementia change this?", a: "Yes. People with Lewy body dementia can react severely to typical antipsychotics, especially haloperidol. The doctor must know the diagnosis." },
        ],
        related: ["sundowning", "refusing-care", "hallucinations", "sleep-disturbance"],
        lane_prompt: "Dad is suddenly agitated — what should I check first?",
        updated_at: "2026-02-20",
    },
    {
        slug: "hallucinations",
        group: "behavior",
        title: "Dementia Hallucinations — What's Real to Them, What Helps",
        meta: "Why people with dementia see, hear, or believe things that aren't there. What's harmless, what's a red flag, and how to respond without arguing.",
        h1: "Hallucinations and false beliefs in dementia",
        summary: "Hallucinations are real to the person experiencing them. Arguing rarely works. Knowing when they're harmless versus a medical emergency does.",
        body: [
            { type: "h2", text: "Types you may see" },
            { type: "ul", items: ["Visual — seeing children, animals, or a deceased relative. Most common in Lewy body dementia.", "Auditory — hearing voices or music.", "Delusions — fixed false beliefs (a spouse is unfaithful, someone is stealing).", "Misidentification — calling you by their deceased sibling's name."] },
            { type: "h2", text: "How to respond" },
            { type: "ol", items: ["Don't argue with the content. \"They look upsetting\" beats \"there's no one there.\"", "Look for the trigger — shadows, a TV in another room, low light.", "Change the channel — walk to a different room together.", "Reassure with presence and touch."] },
            { type: "callout", tone: "coral", text: "New hallucinations + fever, confusion, sudden change in alertness = medical emergency. ER, same day. This pattern is often infection or a new medication." },
        ],
        faq: [
            { q: "Should we tell the doctor about every hallucination?", a: "Yes — pattern matters. Visual + REM-sleep behavior + Parkinson features point toward Lewy body dementia, which changes treatment choices." },
            { q: "Is a TV making it worse?", a: "Often, yes. People with dementia may experience TV scenes as happening in the room. Try music or audiobooks instead." },
        ],
        related: ["sundowning", "agitation", "alzheimers-vs-dementia"],
        lane_prompt: "Mom keeps seeing people who aren't there — should I be worried?",
        updated_at: "2026-02-20",
    },
    {
        slug: "refusing-bath",
        group: "behavior",
        title: "When a Loved One with Dementia Refuses to Bathe",
        meta: "Bathing refusal is one of the hardest battles. Why it happens, how to make it gentler, and the techniques that actually work — without losing the relationship.",
        h1: "Bathing refusal — what's really going on",
        summary: "Bathing involves cold, nakedness, water on the face, balance, and trust. Refusal is rarely about cleanliness — it's about feeling unsafe.",
        body: [
            { type: "h2", text: "What makes bathing hard for them" },
            { type: "ul", items: ["Cold — the room, the air, the wet skin.", "Nakedness in front of someone (especially an adult child).", "Water hitting the face triggers a startle reflex.", "Slippery surfaces feel dangerous.", "Memory loss means every shower feels new and unfamiliar."] },
            { type: "h2", text: "What to try this week" },
            { type: "ol", items: ["Warm the bathroom 10 minutes before. Warm towels in the dryer.", "Sponge bath at the sink instead of a shower.", "Use a handheld shower head pointed AWAY from the face.", "Wash hair on a different day from body.", "Use a tub bench or shower chair — standing is exhausting.", "Same time of day, same caregiver, same routine."] },
            { type: "callout", tone: "teal", text: "If they go 2–3 days without a full bath, that's OK. Focus on what matters daily: face, hands, perineum. Their dignity matters more than your schedule." },
        ],
        faq: [
            { q: "Is twice a week enough?", a: "For most older adults, yes. Daily showers dry out skin and increase fall risk. 2–3 full baths a week is medically fine." },
            { q: "What if they get aggressive?", a: "Stop. Walk away. Try again later or tomorrow. Forcing a bath risks injury for both of you." },
        ],
        related: ["refusing-care", "agitation", "sundowning"],
        lane_prompt: "Mom hasn't bathed in 4 days — give me 5 things to try before forcing it.",
        updated_at: "2026-02-20",
    },
    {
        slug: "sleep-disturbance",
        group: "behavior",
        title: "Dementia and Sleep — Up at Night, Drowsy by Day",
        meta: "Why dementia flips sleep upside-down and what helps — light, schedule, melatonin, when to call the doctor, and what to avoid (benzodiazepines, alcohol).",
        h1: "When dementia turns night into day",
        summary: "The brain's internal clock breaks down in dementia. Most caregivers eventually face a stretch of nights with someone wandering, asking questions, or simply awake.",
        body: [
            { type: "h2", text: "What to try first" },
            { type: "ol", items: ["Bright light at breakfast — light box or sunlight near a window for 20–30 minutes.", "No caffeine after noon.", "Short afternoon nap (≤30 min) before 3 PM. Skip the long nap.", "Wind-down routine — same music, dim lights, same chair for an hour before bed.", "Bathroom right before bed; limit fluids after 6 PM."] },
            { type: "h2", text: "Medications: what helps, what hurts" },
            { type: "ul", items: ["Melatonin 0.5–3 mg, 30 min before bed — modest help for some.", "Trazodone — sometimes used at low dose under doctor supervision.", "Avoid benzodiazepines (Ativan, Xanax, Restoril) as first-line — falls + worse confusion.", "Avoid alcohol as a sleep aid — fragments sleep and worsens dementia."] },
            { type: "callout", tone: "coral", text: "Acting out dreams (kicking, shouting, falling out of bed) is REM sleep behavior disorder. It often appears YEARS before Lewy body dementia. Tell the neurologist — it changes diagnosis." },
        ],
        faq: [
            { q: "Can my loved one be \"sun-downed\" all night?", a: "Yes — agitation that started at sunset can roll into nighttime wakefulness. Treat the underlying triggers (pain, full bladder, too much input)." },
            { q: "Is it safe to sleep when they're awake?", a: "Use door alarms and a baby monitor. Some caregivers sleep in shifts with family. Long-term sleep deprivation is dangerous for caregivers." },
        ],
        related: ["sundowning", "wandering", "lewy-body-dementia"],
        lane_prompt: "Mom is up every night. Help me figure out the right plan.",
        updated_at: "2026-02-20",
    },
    {
        slug: "aggression",
        group: "behavior",
        title: "Aggression in Dementia — What to Do When It Gets Physical",
        meta: "Why people with dementia sometimes hit, shove, or yell — and what helps. When to step back, when to call for help, and the safety plan every family needs.",
        h1: "Aggression — staying safe, staying connected",
        summary: "Physical aggression in dementia is almost always a panic response, not a personality change. Knowing the difference keeps both of you safe.",
        body: [
            { type: "h2", text: "Read the warning signs early" },
            { type: "ul", items: ["Pacing, clenched fists, rapid breathing.", "Loud voice, refusing to make eye contact.", "Repeating a phrase like \"no, no, no\".", "Backing into a corner."] },
            { type: "h2", text: "Step-by-step de-escalation" },
            { type: "ol", items: ["Step back. Give space. Don't crowd them.", "Lower your voice and slow your speech.", "Drop the task — bath, meds, dinner — it can wait.", "Move to a quieter space. Sit down.", "Reassure with simple phrases: \"You're safe. I'm here.\"", "If you can't de-escalate, leave the room briefly."] },
            { type: "callout", tone: "coral", text: "If you're being hurt — call 911 and ask for a CIT-trained officer if available. Tell dispatch \"dementia patient in crisis.\" This is not failure. This is safety." },
        ],
        faq: [
            { q: "Should I take it personally?", a: "No. Aggression is a symptom of the disease damaging the brain regions that regulate fear and impulse. Easier said than done — but try." },
            { q: "When is medication appropriate?", a: "When safety can't be maintained otherwise. The conversation belongs with a geriatric psychiatrist or the GUIDE care navigator." },
        ],
        related: ["agitation", "sundowning", "refusing-care"],
        lane_prompt: "Dad pushed me yesterday. Help me figure out what to do next.",
        updated_at: "2026-02-20",
    },
    {
        slug: "repetitive-questions",
        group: "behavior",
        title: "Repetitive Questions — Why They Happen & How to Respond",
        meta: "When a loved one with dementia asks the same question every 90 seconds — what's happening in the brain, what works, and how to stay sane.",
        h1: "Repetitive questions — staying patient with the 50th \"what time is it?\"",
        summary: "Short-term memory holds new information for less than a minute in moderate dementia. To them, each question is the first time.",
        body: [
            { type: "h2", text: "What helps the question stop coming" },
            { type: "ol", items: ["Answer simply and once: \"It's 2 o'clock.\"", "Show, don't explain. A large clock or whiteboard with the day's plan.", "Redirect to an activity, song, or snack — the new input replaces the question.", "Accept that some questions will recur no matter what."] },
            { type: "h2", text: "What helps YOU stay patient" },
            { type: "ul", items: ["Notice you're being asked something — they're not trying to annoy you.", "Schedule breaks for yourself in the day.", "Use respite care so you're not the only voice."] },
        ],
        faq: [
            { q: "Is it normal to feel angry about this?", a: "Yes. Every caregiver does. The anger is at the disease, not the person. Saying that out loud helps." },
        ],
        related: ["sundowning", "agitation"],
        lane_prompt: "Mom asks me the same question every minute. Help me cope.",
        updated_at: "2026-02-20",
    },

    // ===== GUIDE — pre-screeners, provider search, FAQ =====
    {
        slug: "pre-screeners",
        group: "guide",
        title: "GUIDE Pre-Screeners — What to Fill Out Before the Visit",
        meta: "What pre-screeners the Medicare GUIDE program uses, how to complete them at home with Memory Lane, and what they tell the care team.",
        h1: "GUIDE pre-screeners — saving 30 minutes at the first visit",
        summary: "GUIDE programs use a small battery of validated screeners to understand cognition, function, mood, and caregiver burden. Doing them at home turns the first visit into a planning visit.",
        body: [
            { type: "h2", text: "What screeners you'll see" },
            { type: "ul", items: ["FAST — Functional Assessment Staging — what they can/can't do.", "Zarit-12 — caregiver burden, short version.", "PHQ-2 / PHQ-9 — caregiver mood.", "GDS-15 — patient mood (geriatric depression scale).", "NPI — neuropsychiatric inventory if behaviors are present."] },
            { type: "h2", text: "Why this matters" },
            { type: "p", text: "Without screeners, the first GUIDE visit becomes intake. With them, it becomes care planning — what respite hours you need, what behavior plan fits, what medication conversations to start." },
            { type: "callout", tone: "teal", text: "Memory Lane lets you complete every GUIDE screener from home — answers feed automatically into the Continuity Passport you can hand to any clinician." },
        ],
        faq: [
            { q: "Will my answers stay private?", a: "Yes. Memory Lane stores screener answers in your Continuity Passport. You choose who to share it with." },
        ],
        related: ["guide-eligibility", "what-is-dementia"],
        lane_prompt: "Help me get ready for my first GUIDE appointment.",
        updated_at: "2026-02-20",
    },
    {
        slug: "provider-search",
        group: "guide",
        title: "Finding a GUIDE Provider Near You",
        meta: "How to find a Medicare GUIDE participating health system in your state, what to ask before enrolling, and how Memory Lane's directory makes it faster.",
        h1: "Finding a GUIDE provider in your state",
        summary: "GUIDE is delivered through participating health systems. The list is growing — but it isn't everywhere yet.",
        body: [
            { type: "h2", text: "Where to look" },
            { type: "ol", items: ["Memory Lane's `/help-finder` — filter by your state and pick the GUIDE chip.", "CMS GUIDE Model directory (innovation.cms.gov).", "Your loved one's current neurologist — many GUIDE practices grew out of memory-care clinics.", "State Alzheimer's Association chapter."] },
            { type: "h2", text: "What to ask before enrolling" },
            { type: "ul", items: ["Who will my care navigator be? How do I reach them?", "Is the 24/7 helpline answered by a nurse or a triage service?", "How is the $2,500/year respite benefit applied?", "Will GUIDE coordinate with my current neurologist?"] },
        ],
        faq: [
            { q: "What if there's no GUIDE provider near me?", a: "Telehealth-based GUIDE programs are emerging. Check the CMS directory for virtual options, and see Memory Lane's telehealth filter." },
        ],
        related: ["guide-eligibility", "guide-faq"],
        lane_prompt: "Help me find a GUIDE provider in my state.",
        updated_at: "2026-02-20",
    },
    {
        slug: "guide-faq",
        group: "guide",
        title: "GUIDE Program — Frequently Asked Questions",
        meta: "Common GUIDE questions answered — Medicare Advantage compatibility, hospice overlap, cost, what GUIDE replaces, and how to switch back to traditional Medicare.",
        h1: "GUIDE — your questions, answered",
        summary: "The Medicare GUIDE model launched in 2024. It's still new — and the rules are still being explained badly. Here are the most common confusions.",
        body: [
            { type: "h2", text: "Top FAQs" },
            { type: "ul", items: ["GUIDE is for the PATIENT with dementia, not the caregiver — but the navigator coordinates with whoever is providing care.", "You cannot be on Medicare Advantage AND GUIDE simultaneously; you must be on traditional Medicare.", "GUIDE doesn't replace the neurologist — the navigator coordinates with them.", "Hospice and GUIDE don't overlap — choosing hospice ends GUIDE eligibility.", "PACE and GUIDE don't overlap either."] },
        ],
        faq: [
            { q: "How do I switch from Medicare Advantage to traditional Medicare for GUIDE?", a: "During the Annual Election Period (Oct 15–Dec 7) or via Special Enrollment if you qualify." },
            { q: "Does GUIDE replace my supplemental (Medigap) coverage?", a: "No. Medigap continues to cover the gaps in traditional Medicare." },
        ],
        related: ["guide-eligibility", "provider-search", "medicare-coverage"],
        lane_prompt: "Quick questions about Medicare GUIDE — help me understand the rules.",
        updated_at: "2026-02-20",
    },

    // ===== PAYING FOR CARE — Medicaid, PACE, LTC, hospice benefit =====
    {
        slug: "medicaid-waivers",
        group: "paying-for-care",
        title: "Medicaid HCBS Waivers for Dementia — How They Actually Work",
        meta: "Medicaid Home and Community-Based Services waivers for dementia caregivers — what they cover, eligibility, and how to apply state by state.",
        h1: "Medicaid HCBS waivers",
        summary: "Medicaid HCBS waivers are how millions of dementia families afford in-home care, adult day, and respite. Eligibility is income + asset based — and the rules are state-specific.",
        body: [
            { type: "h2", text: "What HCBS waivers can pay for" },
            { type: "ul", items: ["In-home personal care assistants.", "Adult day health programs.", "Respite — both in-home and overnight at a facility.", "Home modifications — grab bars, ramps, accessible bathrooms.", "Sometimes a family member as a paid caregiver (Consumer-Directed Care)."] },
            { type: "h2", text: "Getting through the application" },
            { type: "ol", items: ["Call your state Medicaid office — ask specifically about \"HCBS waivers for dementia.\"", "Expect a waitlist in some states.", "An elder-law attorney can help structure assets if you're close to the income/asset thresholds.", "Memory Lane's Care Team Summary helps speed up the medical documentation."] },
        ],
        faq: [
            { q: "Is there an asset limit?", a: "Yes — varies by state, typically $2,000 individual, $3,000 couple, but home and one car are usually excluded. Some states are more generous." },
        ],
        related: ["medicare-coverage", "va-aid-and-attendance", "finding-respite"],
        lane_prompt: "Help me figure out if mom qualifies for Medicaid HCBS in my state.",
        updated_at: "2026-02-20",
    },
    {
        slug: "pace",
        group: "paying-for-care",
        title: "PACE — Program of All-Inclusive Care for the Elderly",
        meta: "PACE wraps medical, social, and long-term care into one program — and many families with dementia find it transformative. Eligibility, how it works, and how it differs from GUIDE.",
        h1: "PACE — full-spectrum care for adults 55+",
        summary: "PACE bundles primary care, specialty care, adult day, transportation, meals, and even nursing home placement if needed. For eligible families, it can be life-changing.",
        body: [
            { type: "h2", text: "Eligibility" },
            { type: "ul", items: ["55 or older.", "Live in a PACE service area.", "Certified as needing nursing-home-level care by your state.", "Able to live safely in the community at the time of enrollment."] },
            { type: "h2", text: "What PACE covers" },
            { type: "ul", items: ["Primary care + most specialists.", "Adult day program with meals and activities.", "Prescription medications.", "Transportation to and from PACE center and medical appointments.", "Home care, social work, physical/occupational therapy.", "Hospital and nursing facility care when needed."] },
            { type: "callout", tone: "teal", text: "PACE and GUIDE can't be combined. PACE is more comprehensive but requires you live in a PACE service area and meet nursing-home-level criteria." },
        ],
        faq: [
            { q: "Does PACE cost anything?", a: "If your loved one qualifies for both Medicare and Medicaid, usually no monthly premium. Otherwise there's a monthly PACE premium." },
        ],
        related: ["medicaid-waivers", "guide-eligibility", "moving-to-memory-care"],
        lane_prompt: "Is PACE right for my mom? How does it compare to GUIDE?",
        updated_at: "2026-02-20",
    },
    {
        slug: "ltc-insurance",
        group: "paying-for-care",
        title: "Long-Term Care Insurance — Worth Filing the Claim For?",
        meta: "How to file a long-term care insurance claim for dementia, what counts as benefit-trigger, common rejection reasons, and the documents you'll need.",
        h1: "Long-term care insurance claims for dementia",
        summary: "If your loved one has an old LTC policy, dust it off. Most policies were sold in the 80s and 90s and have generous benefits — but the claim process is intentionally complicated.",
        body: [
            { type: "h2", text: "Documenting the benefit trigger" },
            { type: "ul", items: ["Most policies pay when the insured needs help with 2+ ADLs (bathing, dressing, eating, toileting, transferring, continence) OR has severe cognitive impairment.", "Get a physician's letter listing specific ADL deficits.", "Most policies require a 30–90 day \"elimination period\" before payments start.", "Some policies require care be delivered by a state-licensed agency."] },
            { type: "h2", text: "Common rejection reasons" },
            { type: "ol", items: ["Documentation isn't specific (\"needs help\" vs \"unable to independently dress without verbal cues\").", "Care delivered by family member when policy requires licensed agency.", "Daily caregiving log not maintained.", "Failure to invoke the policy in writing within stated timeline."] },
        ],
        faq: [
            { q: "Should I hire someone to help with the claim?", a: "Often yes. Specialized claim consultants charge a fee but understand the rejection patterns." },
        ],
        related: ["va-aid-and-attendance", "medicare-coverage", "medicaid-waivers"],
        lane_prompt: "Help me file a long-term care insurance claim.",
        updated_at: "2026-02-20",
    },
    {
        slug: "hospice-benefit",
        group: "paying-for-care",
        title: "The Medicare Hospice Benefit — What's Actually Covered",
        meta: "What the Medicare Hospice Benefit pays for, what families still pay out of pocket, and the common myths (hospice isn't giving up).",
        h1: "The Medicare Hospice Benefit",
        summary: "Medicare's hospice benefit is one of the most generous things Medicare does — and one of the most misunderstood.",
        body: [
            { type: "h2", text: "What hospice covers" },
            { type: "ul", items: ["Nurse + aide + chaplain + social worker visits at home.", "24/7 phone access; on-call nurse visits as needed.", "All medications related to the hospice diagnosis.", "Medical equipment — hospital bed, oxygen, wheelchair, hoyer lift.", "Up to 5 days respite care in a facility per benefit period."] },
            { type: "h2", text: "What hospice does NOT cover" },
            { type: "ul", items: ["Custodial care 24/7 — family still does most of the hands-on care.", "Treatments for the terminal illness (chemo, dialysis, etc.).", "Room and board if you choose to be in a residential hospice facility."] },
            { type: "callout", tone: "teal", text: "Choosing hospice doesn't mean giving up. You can revoke at any time and resume curative care. Many families wish they'd called hospice sooner." },
        ],
        faq: [
            { q: "Does hospice mean nobody comes from Medicare anymore?", a: "No. The hospice team is paid by Medicare. The regular doctor can stay involved." },
            { q: "Can dementia patients be on hospice for over a year?", a: "Yes. The six-month prognosis is reviewed periodically; recertification is routine." },
        ],
        related: ["home-health-vs-hospice", "stages-of-dementia", "end-of-life-wishes"],
        lane_prompt: "Help me understand if hospice is right for my dad.",
        updated_at: "2026-02-20",
    },

    // ===== TREATMENTS — Kisunla, Exelon, Memantine, Rexulti =====
    {
        slug: "donanemab-kisunla",
        group: "treatments",
        title: "Donanemab (Kisunla) for Alzheimer's — What Families Need to Know",
        meta: "Donanemab (Kisunla) explained — how it works, who qualifies, monthly infusion logistics, ARIA monitoring, and how it compares to Lecanemab.",
        h1: "Donanemab (Kisunla)",
        summary: "Donanemab is the second FDA-approved amyloid-clearing infusion for early Alzheimer's. Unlike Lecanemab, it stops once amyloid is cleared from the brain.",
        body: [
            { type: "h2", text: "How it works" },
            { type: "p", text: "Donanemab (Kisunla) is a monthly IV infusion that targets amyloid plaques. Trials showed about 35% slower decline over 18 months in patients with low-to-medium tau burden." },
            { type: "h2", text: "Who qualifies" },
            { type: "ul", items: ["Confirmed early-stage Alzheimer's.", "Amyloid biomarker positive (PET or CSF).", "Able to attend monthly infusions and frequent MRIs.", "Not on chronic anticoagulation in most clinics."] },
            { type: "callout", tone: "coral", text: "Donanemab carries a higher ARIA (brain swelling/microbleeds) risk than Lecanemab — about 24% on imaging. APOE-ε4 homozygotes face elevated risk." },
        ],
        faq: [
            { q: "Does Medicare cover Kisunla?", a: "Yes, for eligible patients with amyloid confirmation and ongoing safety monitoring." },
            { q: "Can I switch from Lecanemab to Donanemab?", a: "Possible, but the neurologist will weigh ARIA history and tau burden first." },
        ],
        related: ["lecanemab-leqembi", "donepezil-aricept", "alzheimers-vs-dementia"],
        lane_prompt: "Compare Donanemab and Lecanemab for my mom.",
        updated_at: "2026-02-20",
    },
    {
        slug: "rivastigmine-exelon",
        group: "treatments",
        title: "Rivastigmine (Exelon) Patch — What Caregivers Need to Know",
        meta: "Rivastigmine for dementia explained — when the Exelon patch helps, how to use it correctly, side effects, and rotation.",
        h1: "Rivastigmine (Exelon)",
        summary: "Rivastigmine works similarly to donepezil but delivered through a daily skin patch. Many families prefer the patch when swallowing pills becomes harder.",
        body: [
            { type: "h2", text: "How it works" },
            { type: "p", text: "Rivastigmine boosts acetylcholine in the brain, like donepezil. Approved for mild-to-moderate Alzheimer's and Parkinson's dementia. The patch delivers steady drug levels with fewer GI side effects than pills." },
            { type: "h2", text: "Using the patch correctly" },
            { type: "ol", items: ["Apply once daily to clean, dry, hairless skin (upper back, chest, or upper arm).", "Rotate the site daily — don't reapply to the same spot for 14 days.", "Wash hands after application — skin reactions are common otherwise.", "Don't cut the patch.", "Dispose of used patches folded sticky-side together so no one (or no pet) gets exposed."] },
        ],
        faq: [
            { q: "Is the patch better than the pill?", a: "For many patients yes — fewer nausea/diarrhea complaints. Same overall benefit." },
            { q: "What if it falls off?", a: "Replace immediately; don't double up. Set a daily phone alarm to check it." },
        ],
        related: ["donepezil-aricept", "lecanemab-leqembi", "alzheimers-vs-dementia"],
        lane_prompt: "How do I use the Exelon patch correctly?",
        updated_at: "2026-02-20",
    },
    {
        slug: "memantine-namenda",
        group: "treatments",
        title: "Memantine (Namenda) for Moderate-to-Severe Dementia",
        meta: "When memantine helps, how it's different from donepezil, common combinations, and what to expect.",
        h1: "Memantine (Namenda)",
        summary: "Memantine is approved for moderate-to-severe Alzheimer's. It works differently from donepezil and is often added on top rather than instead.",
        body: [
            { type: "h2", text: "How it works" },
            { type: "p", text: "Memantine modulates glutamate — a different brain chemical from acetylcholine. It can soften agitation, improve attention, and reduce some behavioral symptoms in moderate-to-severe disease." },
            { type: "h2", text: "Starting it" },
            { type: "ul", items: ["Usually titrated up from 5 mg to 20 mg/day over 4 weeks.", "Often combined with donepezil — there's a combination pill, Namzaric.", "Generally well-tolerated. Less nausea than cholinesterase inhibitors.", "Watch for dizziness, confusion, headache initially."] },
        ],
        faq: [
            { q: "Will it help in early-stage?", a: "Memantine isn't approved or well-studied in early-stage Alzheimer's. Most prescribers wait for moderate disease." },
        ],
        related: ["donepezil-aricept", "rivastigmine-exelon", "stages-of-dementia"],
        lane_prompt: "Is Namenda right for my mom?",
        updated_at: "2026-02-20",
    },
    {
        slug: "brexpiprazole-rexulti",
        group: "treatments",
        title: "Brexpiprazole (Rexulti) for Alzheimer's Agitation",
        meta: "Rexulti is FDA-approved for agitation in Alzheimer's — how it works, who it's for, the risks, and what to try before reaching for it.",
        h1: "Brexpiprazole (Rexulti) for Alzheimer's agitation",
        summary: "Brexpiprazole became the first FDA-approved medication for Alzheimer's-related agitation in 2023. It's helpful for some — but it's a serious medication.",
        body: [
            { type: "h2", text: "What it does" },
            { type: "p", text: "Brexpiprazole is an atypical antipsychotic that, in trials, reduced agitation scores in Alzheimer's. Effects are modest. Side effects are real." },
            { type: "h2", text: "Before reaching for it" },
            { type: "ul", items: ["Rule out infection, pain, and constipation.", "Try environmental + behavioral interventions for several weeks.", "Audit current medications for anticholinergic burden.", "Discuss the boxed warning — increased mortality in older adults with dementia-related psychosis."] },
            { type: "callout", tone: "coral", text: "All atypical antipsychotics carry a boxed warning in dementia patients. The decision is a careful conversation with a geriatric psychiatrist or GUIDE care navigator." },
        ],
        faq: [
            { q: "Is Rexulti better than older antipsychotics like Risperdal?", a: "It's the only one specifically FDA-approved for this use. Comparison studies are limited." },
        ],
        related: ["agitation", "aggression", "alzheimers-vs-dementia"],
        lane_prompt: "Doctor mentioned Rexulti for mom's agitation — help me weigh it.",
        updated_at: "2026-02-20",
    },

    // ===== HARD CONVERSATIONS — POA, siblings, memory care, end-of-life, take the keys =====
    {
        slug: "power-of-attorney",
        group: "hard-conversations",
        title: "Power of Attorney for Dementia — Don't Wait",
        meta: "Why power of attorney for a dementia parent has a time limit, healthcare proxy vs financial POA, and what happens if you wait too long (guardianship).",
        h1: "Power of attorney — the conversation with a clock",
        summary: "POA can only be signed while your loved one still has capacity. The moment they lose it, your only path is guardianship — slow, expensive, public.",
        body: [
            { type: "h2", text: "Two different documents" },
            { type: "ul", items: ["Financial POA — manages money, pays bills, files taxes, sells assets.", "Healthcare POA / Healthcare proxy — makes medical decisions when they can't.", "Both can be \"durable\" — meaning they stay active once they sign.", "Most states accept both being held by the same person."] },
            { type: "h2", text: "How to have the conversation" },
            { type: "ol", items: ["Frame it as planning, not loss: \"While you're feeling clear, let's make sure your voice is the one in charge.\"", "Use an elder-law attorney — about $400–$1,200 for both documents.", "Involve all siblings in the conversation, even if only one is named POA.", "Update advance directive at the same time."] },
            { type: "callout", tone: "coral", text: "If you wait until they can't sign, your option is guardianship — court, attorney fees, ongoing oversight, $5,000–$15,000 typically. Skip that by acting now." },
        ],
        faq: [
            { q: "Can a person with mild dementia still sign POA?", a: "Often yes — the bar is \"understanding the document at the time of signing.\" An attorney can assess. Mild cognitive impairment usually qualifies." },
        ],
        related: ["sharing-the-diagnosis", "stop-driving", "end-of-life-wishes"],
        lane_prompt: "I need to get power of attorney for my mom. Walk me through it.",
        updated_at: "2026-02-20",
    },
    {
        slug: "talking-with-siblings",
        group: "hard-conversations",
        title: "Talking with Siblings About Mom or Dad's Dementia",
        meta: "Why sibling conflict is the #2 stressor in dementia caregiving, how to run a useful family meeting, and what to do when siblings disagree on the plan.",
        h1: "When siblings see it differently",
        summary: "The siblings who live nearby see the worst of dementia. The ones far away often arrive convinced \"mom seems fine.\" Bridging that gap is its own job.",
        body: [
            { type: "h2", text: "Why the gap exists" },
            { type: "ul", items: ["People with dementia \"show-time\" — they pull it together for a visit then crash after.", "The primary caregiver sees the unwell hours; the visiting sibling sees the showtime.", "Old family dynamics flare up under stress.", "Money — about future inheritance — sits underneath every disagreement."] },
            { type: "h2", text: "Running a family meeting" },
            { type: "ol", items: ["Set an agenda in writing — what we'll decide and what we'll table.", "Share the Care Team Summary from Memory Lane so everyone sees the same data.", "Use a facilitator if the conversation has been derailing — Aging Life Care Manager, social worker, or therapist.", "End with named tasks: who calls Medicare, who finds respite, who visits on which weekends."] },
            { type: "callout", tone: "teal", text: "Memory Lane's Care Team Summary is built for this exact moment — one document, shared, that gives every sibling the same picture of where mom or dad is on the journey." },
        ],
        faq: [
            { q: "What if one sibling refuses to help?", a: "Some never will. Stop trying to convert them. Focus your energy on what you can control." },
        ],
        related: ["sharing-the-diagnosis", "power-of-attorney", "moving-to-memory-care"],
        lane_prompt: "My sister doesn't believe mom has dementia. Help me have the conversation.",
        updated_at: "2026-02-20",
    },
    {
        slug: "moving-to-memory-care",
        group: "hard-conversations",
        title: "When to Move a Loved One to Memory Care",
        meta: "How to know it's time for memory care, how to choose a facility, the move-in week, and the guilt that comes with it.",
        h1: "Moving to memory care — knowing when, choosing where",
        summary: "Most families wait too long. The signs that it's time often pile up gradually until something — a fall, a fire on the stove, a wandering episode — forces the conversation.",
        body: [
            { type: "h2", text: "Signs it's time" },
            { type: "ul", items: ["Safety incidents — stove left on, leaving the house, falls.", "Caregiver health is failing — sleep, weight, blood pressure.", "Aggression that cannot be managed at home.", "Toileting needs more than one person can provide.", "Wandering at night."] },
            { type: "h2", text: "How to choose" },
            { type: "ol", items: ["Visit at meal time and shift change — that's when staffing is real.", "Ask the staff-to-resident ratio specifically on the memory-care unit.", "Watch how staff speak to residents who can't answer.", "Ask about the activity calendar — meaningful engagement, not just TV.", "Read the most recent state inspection report."] },
            { type: "callout", tone: "coral", text: "The guilt is universal. Choosing memory care is not abandoning them. It's choosing a level of safety and connection you can't sustain alone." },
        ],
        faq: [
            { q: "How much does memory care cost?", a: "National average ~$6,000-$8,000/month. Varies widely by state, ranges $4,000–$15,000+. VA, LTC insurance, and Medicaid waivers help for eligible families." },
        ],
        related: ["finding-respite", "pace", "ltc-insurance", "stop-driving"],
        lane_prompt: "How do I know if it's time for memory care?",
        updated_at: "2026-02-20",
    },
    {
        slug: "end-of-life-wishes",
        group: "hard-conversations",
        title: "Advance Directives & End-of-Life Wishes for Dementia",
        meta: "How to talk about end-of-life wishes while there's still time, advance directive vs POLST, feeding tubes, and the conversations every family should have.",
        h1: "End-of-life wishes — saying it while they can still say it",
        summary: "Dementia gives families a long runway to talk about end of life. Most families never use it. The conversation feels heavy — but it relieves the heaviest weight later.",
        body: [
            { type: "h2", text: "Documents to have" },
            { type: "ul", items: ["Advance directive (living will) — broad wishes about life support.", "Healthcare POA — names a decision-maker.", "POLST / MOLST — physician orders specifying CPR, intubation, antibiotics, feeding tube. Pink/bright-colored, lives on the fridge.", "Five Wishes — a friendlier values-based document many families find easier to start with."] },
            { type: "h2", text: "Questions to walk through together" },
            { type: "ol", items: ["What does \"a good day\" look like to you now? What would make it not a good day anymore?", "If you couldn't recognize family — would you want everything done, or comfort?", "Hospital vs at home for the last weeks?", "What music, smells, voices do you want around you?"] },
        ],
        faq: [
            { q: "Should mom have a feeding tube in advanced dementia?", a: "Most expert guidelines (AGS, AAHPM) recommend against feeding tubes in advanced dementia — they don't extend life and add complications. Hand-feeding is gentler. Talk with the medical team early." },
        ],
        related: ["power-of-attorney", "hospice-benefit", "stages-of-dementia"],
        lane_prompt: "Help me start the end-of-life conversation with dad while he can still answer.",
        updated_at: "2026-02-20",
    },
    {
        slug: "when-to-take-the-keys",
        group: "hard-conversations",
        title: "When to Take the Car Keys from Someone with Dementia",
        meta: "Signs the time has come, what to try first, and how to take the keys without losing the relationship.",
        h1: "Taking the keys — the line, and what's on the other side",
        summary: "Driving is identity. Taking the keys is one of the hardest single moments in the dementia journey — but waiting too long can be catastrophic.",
        body: [
            { type: "h2", text: "Today's red flags" },
            { type: "ul", items: ["New dents on the car they can't explain.", "Getting lost on familiar routes.", "Pumping gas at the wrong pump or driving away with the hose.", "Friends or family asking \"is it still safe?\"", "An at-fault accident, even minor."] },
            { type: "h2", text: "How to take them" },
            { type: "ol", items: ["Request a driving evaluation from an Occupational Therapist — external authority removes blame.", "If unsafe today, ask the doctor to write a letter recommending stop driving.", "Some states allow physician reporting to DMV.", "Disable the car — battery cable removed, sell it, or move it out of sight."] },
            { type: "callout", tone: "teal", text: "Replace the freedom before you take it. Set up Uber Health, GoGoGrandparent, or a volunteer driver service. Driving cessation grief is real — have something else to drive toward." },
        ],
        faq: [
            { q: "Should I just hide the keys?", a: "It works for a week or two. After that the person notices, accuses you of stealing them, or finds them. Better to make it formal." },
        ],
        related: ["stop-driving", "wandering", "moving-to-memory-care"],
        lane_prompt: "Help me figure out if it's time to take dad's keys, and how to do it.",
        updated_at: "2026-02-20",
    },

    // ===== LEARN — Lewy body, FTD, vascular, MCI, early warning signs =====
    {
        slug: "lewy-body-dementia",
        group: "learn",
        title: "Lewy Body Dementia — Plain-English Guide",
        meta: "Lewy body dementia explained for caregivers — hallmark symptoms, what's different from Alzheimer's, medications that help (and ones that can be dangerous).",
        h1: "Lewy body dementia",
        summary: "Lewy body dementia (LBD) is the third most common dementia. It shows up differently — visual hallucinations, sleep behaviors, Parkinson-like stiffness — and is dangerous to treat with the wrong medications.",
        body: [
            { type: "h2", text: "Hallmark signs" },
            { type: "ul", items: ["Fluctuating cognition — sharp one hour, confused the next.", "Vivid visual hallucinations — often of people, children, or animals.", "REM sleep behavior disorder — acting out dreams, sometimes years before diagnosis.", "Parkinson-like features — shuffling gait, stiffness, falls.", "Sensitivity to antipsychotics — especially Haldol — can cause severe rigidity."] },
            { type: "callout", tone: "coral", text: "If your loved one has LBD: every doctor, dentist, and ER nurse must know. Wear a medical alert bracelet. Carry a wallet card listing dangerous medications." },
        ],
        faq: [
            { q: "Is LBD inherited?", a: "Mostly sporadic. Family history slightly increases risk but it's not Mendelian." },
            { q: "What about medications that help?", a: "Rivastigmine (Exelon) often works dramatically well for LBD cognition. Pimavanserin can help hallucinations. AVOID typical antipsychotics." },
        ],
        related: ["what-is-dementia", "alzheimers-vs-dementia", "hallucinations", "rivastigmine-exelon"],
        lane_prompt: "Mom was just diagnosed with Lewy body dementia — what should I know?",
        updated_at: "2026-02-20",
    },
    {
        slug: "frontotemporal-dementia",
        group: "learn",
        title: "Frontotemporal Dementia (FTD) — When Personality Changes Come First",
        meta: "FTD explained for families — why personality, judgment, or language changes before memory, how it's different from Alzheimer's, and where to find specialized care.",
        h1: "Frontotemporal dementia (FTD)",
        summary: "FTD is the most common dementia in people under 60. The earliest signs are personality, judgment, or language — not memory. Many families think it's depression or a midlife crisis first.",
        body: [
            { type: "h2", text: "Two main forms" },
            { type: "ul", items: ["Behavioral variant — personality changes, social inappropriateness, loss of empathy, food obsessions.", "Primary progressive aphasia — language fails first. Word-finding, naming, or grammar disappear over years."] },
            { type: "h2", text: "What makes FTD especially hard" },
            { type: "ul", items: ["Often misdiagnosed for years as depression, bipolar, or marital problems.", "Onset 45–65 — many families have school-age kids and active careers.", "Cholinesterase inhibitors (donepezil) often don't help and may worsen behavior."] },
        ],
        faq: [
            { q: "Is there a genetic test?", a: "Yes — for some forms (C9orf72, MAPT, GRN). Discuss with a genetic counselor before testing." },
        ],
        related: ["what-is-dementia", "alzheimers-vs-dementia", "lewy-body-dementia"],
        lane_prompt: "My husband was diagnosed with FTD at 58. Help me understand what's ahead.",
        updated_at: "2026-02-20",
    },
    {
        slug: "vascular-dementia",
        group: "learn",
        title: "Vascular Dementia — When Strokes Cause Cognitive Decline",
        meta: "Vascular dementia explained for caregivers — risk factors, the stair-step pattern, prevention, and how it overlaps with Alzheimer's.",
        h1: "Vascular dementia",
        summary: "Vascular dementia is caused by reduced blood flow to the brain — strokes, small-vessel disease, or both. It often shows up as a stair-step decline rather than gradual.",
        body: [
            { type: "h2", text: "Signs and pattern" },
            { type: "ul", items: ["Stair-step worsening — sudden declines followed by plateaus.", "Slowed thinking, planning difficulty BEFORE memory loss.", "Walking changes — slowed gait, balance issues.", "Mood changes, apathy, sometimes depression."] },
            { type: "h2", text: "Prevention and treatment" },
            { type: "ul", items: ["Blood pressure control is the single biggest lever.", "Statins for cholesterol when indicated.", "Diabetes management.", "Aspirin or anticoagulation in some cases — the doctor decides.", "Smoking cessation."] },
        ],
        faq: [
            { q: "Can vascular dementia be reversed?", a: "Not reversed — but well-managed BP, diabetes, and cholesterol can slow progression substantially." },
        ],
        related: ["what-is-dementia", "alzheimers-vs-dementia", "stages-of-dementia"],
        lane_prompt: "Dad had two strokes and now has vascular dementia. Help me understand.",
        updated_at: "2026-02-20",
    },
    {
        slug: "mci-mild-cognitive-impairment",
        group: "learn",
        title: "Mild Cognitive Impairment (MCI) — Worried But Not Yet Dementia",
        meta: "MCI explained — what it means, the chances it progresses, what to do now, and why early identification opens treatment options.",
        h1: "Mild cognitive impairment",
        summary: "MCI is the in-between space — symptoms beyond normal aging, but not yet dementia. About 1 in 3 MCI patients progress to dementia within 5 years. Early action matters.",
        body: [
            { type: "h2", text: "What MCI looks like" },
            { type: "ul", items: ["Forgetfulness others notice.", "Still independent in daily activities.", "Cognitive testing shows specific deficits.", "Symptoms aren't due to delirium, medication, or depression."] },
            { type: "h2", text: "What to do now" },
            { type: "ol", items: ["Get a biomarker workup — amyloid PET or CSF — to know if Alzheimer's pathology is present.", "If amyloid-positive and early — Lecanemab or Donanemab may be options.", "Audit medications with a pharmacist.", "Treat sleep apnea, depression, hearing loss aggressively.", "Sign POA + healthcare proxy while easy."] },
        ],
        faq: [
            { q: "Does everyone with MCI get dementia?", a: "No. About a third progress within 5 years, a third stay stable, a third revert to normal." },
        ],
        related: ["what-is-dementia", "alzheimers-vs-dementia", "lecanemab-leqembi", "power-of-attorney"],
        lane_prompt: "Mom just got an MCI diagnosis — what do we do now?",
        updated_at: "2026-02-20",
    },
    {
        slug: "early-warning-signs",
        group: "learn",
        title: "Early Warning Signs of Dementia — When to See a Doctor",
        meta: "The 10 early warning signs of dementia, how they differ from normal aging, and what to expect at the first doctor's visit.",
        h1: "Early warning signs of dementia",
        summary: "Normal aging includes occasional name forgetfulness. Dementia is something different. Knowing the difference saves families months of denial.",
        body: [
            { type: "h2", text: "Ten signs worth checking" },
            { type: "ol", items: ["Memory loss that disrupts daily life — missing appointments, repeating questions.", "Trouble with familiar tasks — paying bills, following a recipe.", "Confusion about time or place.", "Difficulty understanding visual images and spatial relationships.", "New problems with words — calling things by the wrong name.", "Misplacing things and being unable to retrace steps.", "Poor judgment — gives away money, dresses inappropriately for weather.", "Withdrawal from work or hobbies.", "Changes in mood or personality.", "Trouble with reasoning — can't figure out the tip on a restaurant bill."] },
            { type: "h2", text: "What to do" },
            { type: "ol", items: ["Document — note specific incidents with dates.", "Schedule a visit with the primary doctor.", "Bring a family member who's noticed the changes.", "Ask for a referral to neurology, geriatrics, or a memory clinic."] },
        ],
        faq: [
            { q: "Could it be something else?", a: "Yes — depression, thyroid disease, B12 deficiency, sleep apnea, urinary tract infection, medication side effects can all mimic dementia. Rule them out first." },
        ],
        related: ["what-is-dementia", "mci-mild-cognitive-impairment", "stages-of-dementia"],
        lane_prompt: "I'm worried about mom — is this dementia or just aging?",
        updated_at: "2026-02-20",
    },

    // ===== RESOURCES — adult day, telehealth, home design =====
    {
        slug: "choosing-adult-day-program",
        group: "resources",
        title: "Choosing an Adult Day Program for Dementia",
        meta: "Adult day programs explained — what they offer, what to look for, how to handle the first day, and how to pay for it.",
        h1: "Adult day programs",
        summary: "Adult day programs are the most under-used and effective tool in dementia caregiving. A few hours of social engagement transforms both your loved one and you.",
        body: [
            { type: "h2", text: "What a good program looks like" },
            { type: "ul", items: ["Trained staff, low staff-to-participant ratio (1:6 or better for memory care).", "Meaningful activities — music, art, gentle exercise — not just TV.", "A real meal, not just snacks.", "Quiet space for downtime.", "Transportation available."] },
            { type: "h2", text: "First-day strategy" },
            { type: "ol", items: ["Tour twice before deciding — once with your loved one, once during a busy activity time.", "Plan for resistance the first 1–2 weeks; most participants settle in by week 3.", "Stay involved — chat with staff weekly about what's working.", "Build the cost into the family budget OR check Medicaid waivers, VA, and grant programs."] },
        ],
        faq: [
            { q: "How much does adult day cost?", a: "National average around $80/day; ranges $50–$150. Medicaid waivers, VA Aid and Attendance, and Alzheimer's Association grants can offset." },
        ],
        related: ["finding-respite", "home-health-vs-hospice", "medicaid-waivers"],
        lane_prompt: "Help me find and choose an adult day program for mom.",
        updated_at: "2026-02-20",
    },
    {
        slug: "telehealth-options",
        group: "resources",
        title: "Telehealth for Dementia Care — When It Works, When It Doesn't",
        meta: "Telehealth in dementia care — for caregivers, neurologist follow-ups, behavioral health, and GUIDE. What's covered, what's not.",
        h1: "Telehealth for dementia families",
        summary: "Telehealth was a pandemic-era band-aid that's become permanent infrastructure. For rural caregivers, mobility-impaired patients, and overnight crises — it's a lifeline.",
        body: [
            { type: "h2", text: "When telehealth works" },
            { type: "ul", items: ["Routine neurology follow-up.", "Medication adjustments.", "Caregiver therapy or support group.", "GUIDE 24/7 nurse line.", "Geriatric psychiatry — especially in rural areas."] },
            { type: "h2", text: "When in-person is better" },
            { type: "ul", items: ["First-time diagnosis evaluation.", "Significant medication change or behavioral crisis.", "Acute illness with confusion.", "Falls or new mobility problems."] },
        ],
        faq: [
            { q: "Does Medicare cover telehealth?", a: "Yes — most Medicare-covered services are also covered by telehealth. Some specialty services depend on your state and the provider type." },
        ],
        related: ["home-health-vs-hospice", "guide-eligibility"],
        lane_prompt: "Help me set up telehealth for mom's next neurology visit.",
        updated_at: "2026-02-20",
    },
    {
        slug: "dementia-friendly-home-design",
        group: "resources",
        title: "Designing a Dementia-Friendly Home — Safety + Calm",
        meta: "Practical home modifications for dementia — lighting, contrast, signage, kitchen safety, bathroom grab bars, wandering prevention. Cheap and expensive options.",
        h1: "Dementia-friendly home design",
        summary: "Small environmental changes prevent big disasters. You don't need a full renovation — most of what helps costs under $200.",
        body: [
            { type: "h2", text: "Quick wins" },
            { type: "ul", items: ["Motion-activated night lights along the path from bed to bathroom.", "Contrasting toilet seat (red on white) — easier to find.", "Velcro shoes — eliminates one frustration.", "Clear glass on cabinet doors so they see what's inside.", "Bright, evenly distributed lighting — avoid harsh shadows.", "Plug-in stove disabling switch."] },
            { type: "h2", text: "Higher-impact changes" },
            { type: "ul", items: ["Grab bars in shower, beside toilet.", "Walk-in shower or tub.", "Door alarms on exits.", "Lock or remove stove and oven gas line.", "Smart-home cameras for monitoring high-risk areas."] },
        ],
        faq: [
            { q: "Will Medicaid pay for home modifications?", a: "Many HCBS waivers cover bathroom modifications, ramps, and other safety modifications. Ask your case manager." },
        ],
        related: ["wandering", "falls-in-dementia", "medicaid-waivers"],
        lane_prompt: "Help me make our home safer for mom.",
        updated_at: "2026-02-20",
    },

];

export const ARTICLES = [
    ...ARTICLES_INLINE,
    ...ARTICLES_BATCH_2,
    ...ARTICLES_BATCH_3,
];

export function findArticle(group, slug) {
    return ARTICLES.find((a) => a.group === group && a.slug === slug) || null;
}

/* Tag auto-derivation. Most articles ship without explicit tags; this
 * function builds a tag set from the slug + group + summary + body so the
 * knowledge-graph functions below can compute related content. Articles
 * CAN ship an explicit `tags: [...]` to override or extend the auto set.
 *
 * Tag vocabulary intentionally overlaps across groups (e.g. "veteran"
 * tags both /paying-for-care/va-aid-and-attendance and any behavior or
 * resource article that mentions VA), which is what threads the
 * knowledge graph together.
 */
const TAG_KEYWORDS = {
    sundowning: ["sundowning", "sundown", "afternoon agitation", "evening agitation"],
    wandering: ["wandering", "wanders", "leaves the house", "gets lost"],
    aggression: ["aggression", "combative", "hitting", "yelling"],
    "refusing-care": ["refuses", "refusing", "won't bathe", "won't shower"],
    hallucinations: ["hallucinations", "seeing things", "lewy"],
    sleep: ["sleep", "insomnia", "sleeping", "nights"],
    safety: ["safety", "fall", "wandering", "driving", "stove"],
    driving: ["driving", "keys", "car", "dmv"],
    "memory-care": ["memory care", "assisted living", "facility"],
    hospice: ["hospice", "end of life", "comfort care"],
    "home-health": ["home health", "skilled nursing", "rehab"],
    respite: ["respite", "caregiver break"],
    "adult-day": ["adult day", "day program"],
    guide: ["guide", "medicare guide", "navigator"],
    medicare: ["medicare", "part a", "part b", "part d"],
    medicaid: ["medicaid", "waiver", "hcbs"],
    veteran: ["veteran", "va aid", "aid and attendance", "vfw", "american legion"],
    alzheimers: ["alzheimer", "alzheimers"],
    lewy: ["lewy body", "lewy"],
    vascular: ["vascular"],
    frontotemporal: ["frontotemporal", "ftd"],
    medication: ["donepezil", "aricept", "rivastigmine", "exelon", "memantine", "namenda", "lecanemab", "leqembi", "donanemab", "kisunla", "brexpiprazole", "rexulti"],
    "amyloid-clearing": ["lecanemab", "leqembi", "donanemab", "kisunla", "amyloid"],
    "stages": ["stage", "stages", "mild", "moderate", "severe"],
    diagnosis: ["diagnosis", "neurologist", "told"],
    legal: ["power of attorney", "poa", "advance directive", "guardianship"],
    "end-of-life": ["end of life", "hospice", "comfort", "polst"],
    appointments: ["appointment", "neurologist visit", "primary care"],
    burnout: ["burnout", "caregiver stress", "exhausted"],
    behavior: ["behavior", "behaviors", "psychiatric"],
    "guide-eligibility": ["guide eligibility", "medicare guide"],
    siblings: ["sibling", "family meeting"],
    "family-conversations": ["family meeting", "siblings"],
};

function autoDeriveTags(article) {
    const haystack = [
        article.slug,
        article.title || "",
        article.h1 || "",
        article.summary || "",
        article.meta || "",
        ...(article.body || []).map((b) => b.text || (b.items || []).join(" ")),
        ...(article.faq || []).flatMap((f) => [f.q, f.a]),
    ].join(" ").toLowerCase();
    const out = new Set(article.tags || []);
    // The group itself is always a tag — anchors cross-group matching.
    out.add(article.group);
    for (const [tag, keywords] of Object.entries(TAG_KEYWORDS)) {
        if (keywords.some((kw) => haystack.includes(kw.toLowerCase()))) {
            out.add(tag);
        }
    }
    return Array.from(out);
}

// Hydrate tags once at module load — readers (relatedArticles, search) can
// rely on every article having a stable .tags array.
ARTICLES.forEach((a) => { a.tags = autoDeriveTags(a); });

export function articlesByGroup(group) {
    return ARTICLES.filter((a) => a.group === group);
}

export function articleUrl(article) {
    return `/${article.group}/${article.slug}`;
}

/* Related-article computation — tag-graph based.
 *
 * 1. Honor any explicit `related` slugs (handcrafted high-signal links).
 * 2. Then auto-fill with tag-overlap matches across ANY group, scored by
 *    shared tag count, capped at 8 total, and de-duplicated.
 *
 * This is what turns the article corpus into a connected knowledge graph
 * instead of isolated pages — a Behavior article auto-surfaces related
 * Medications, Benefits, and Hard Conversations the moment those entries
 * share a tag like "sundowning", "memory-care", "veteran", etc.
 */
export function relatedArticles(article, limit = 8) {
    if (!article) return [];
    const explicit = (article.related || [])
        .map((slug) => ARTICLES.find((a) => a.slug === slug))
        .filter(Boolean);
    const tags = new Set(article.tags || []);
    if (tags.size === 0) return explicit.slice(0, limit);
    const scored = ARTICLES
        .filter((a) => a.slug !== article.slug)
        .map((a) => {
            const overlap = (a.tags || []).reduce((n, t) => n + (tags.has(t) ? 1 : 0), 0);
            return { a, overlap };
        })
        .filter((s) => s.overlap > 0)
        .sort((x, y) => y.overlap - x.overlap);
    const seen = new Set(explicit.map((e) => e.slug));
    const merged = [...explicit];
    for (const { a } of scored) {
        if (merged.length >= limit) break;
        if (seen.has(a.slug)) continue;
        seen.add(a.slug);
        merged.push(a);
    }
    return merged.slice(0, limit);
}

/* Group-by-section helper for the "Related across the journey" block.
 * Returns an object keyed by group with arrays of matched articles. */
export function relatedByGroup(article, limit = 8) {
    const rels = relatedArticles(article, 30);
    const out = {};
    for (const a of rels) {
        if (a.group === article.group) continue; // skip same-group for cross-section discovery
        (out[a.group] = out[a.group] || []).push(a);
    }
    // cap each group at 3 for visual balance
    Object.keys(out).forEach((g) => { out[g] = out[g].slice(0, 3); });
    return out;
}

/* Search the article corpus — used by Universal Search on the homepage.
 * Token-based fuzzy match across title, summary, meta, body text, tags
 * and FAQ. Returns scored matches; caller can limit. */
export function searchArticles(query, limit = 10, corpus = ARTICLES) {
    const q = (query || "").toLowerCase().trim();
    if (!q) return [];
    const tokens = q.split(/\s+/).filter(Boolean);
    const scored = corpus.map((a) => {
        const haystack = [
            a.title, a.h1, a.summary, a.meta,
            ...(a.tags || []),
            ...(a.faq || []).map((f) => `${f.q} ${f.a}`),
            ...(a.body || []).map((b) => b.text || (b.items || []).join(" ")),
        ].join(" ").toLowerCase();
        let score = 0;
        for (const t of tokens) {
            if (!t) continue;
            // Title hits weighted higher than body
            if (a.title.toLowerCase().includes(t)) score += 5;
            if ((a.tags || []).includes(t)) score += 4;
            if ((a.h1 || "").toLowerCase().includes(t)) score += 3;
            const occ = haystack.split(t).length - 1;
            score += Math.min(occ, 6);
        }
        return { article: a, score };
    }).filter((s) => s.score > 0)
      .sort((x, y) => y.score - x.score);
    return scored.slice(0, limit).map((s) => s.article);
}
